import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiError } from '../api/apiClient';
import { validateInvitation } from '../api/authApi';
import { useAuth } from '../auth/AuthContext';
import type { InvitationValidationResponse } from '../types/auth';
import './AcceptInvitation.css';

const INVALID_INVITATION_MESSAGE =
  'This invitation link is invalid or has expired. Ask your Strata contact for a new invitation.';
const ADMIN_REVIEW_MESSAGE =
  'This invitation cannot be accepted because the account needs administrator review. Contact your Strata administrator.';
const VALIDATION_FAILURE_MESSAGE =
  'We could not verify this invitation right now. Check your connection and try the link again.';
const ACCEPTANCE_FAILURE_MESSAGE =
  'We could not create your account right now. Please try again.';
const PASSWORD_POLICY_MESSAGE =
  'Use 12 to 128 characters and at least two of: lowercase, uppercase, number, or symbol.';

interface CapturedToken {
  value: string | null;
  isInvalid: boolean;
}

function captureToken(search: string): CapturedToken {
  const values = new URLSearchParams(search).getAll('token');

  if (values.length === 0) {
    return { value: null, isInvalid: false };
  }

  const value = values[0];
  if (values.length !== 1 || value.trim().length === 0 || value.length > 512) {
    return { value: null, isInvalid: true };
  }

  return { value, isInvalid: false };
}

function readErrorCode(error: unknown): string | null {
  if (!(error instanceof ApiError) || typeof error.details !== 'object' || error.details === null) {
    return null;
  }

  if ('code' in error.details && typeof error.details.code === 'string') {
    return error.details.code;
  }

  return null;
}

function validationErrorMessage(error: unknown): string {
  if (readErrorCode(error) === 'INVALID_INVITATION_TOKEN') {
    return INVALID_INVITATION_MESSAGE;
  }

  return VALIDATION_FAILURE_MESSAGE;
}

function acceptanceErrorMessage(error: unknown): string {
  const code = readErrorCode(error);

  if (code === 'INVALID_INVITATION_TOKEN') {
    return INVALID_INVITATION_MESSAGE;
  }

  if (code === 'WEAK_PASSWORD') {
    return PASSWORD_POLICY_MESSAGE;
  }

  if (code === 'ORGANIZATION_REQUIRED') {
    return 'Enter your organization.';
  }

  if (error instanceof ApiError && error.status === 409) {
    return ADMIN_REVIEW_MESSAGE;
  }

  return ACCEPTANCE_FAILURE_MESSAGE;
}

function formatRole(role: string): string {
  const words = role.replace(/_/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function AcceptInvitation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { acceptInvitation, isLoading: isAuthLoading } = useAuth();
  const [capturedToken] = useState(() => captureToken(location.search));
  const [invitation, setInvitation] = useState<InvitationValidationResponse | null>(null);
  const [validationError, setValidationError] = useState(() => {
    if (capturedToken.isInvalid) {
      return INVALID_INVITATION_MESSAGE;
    }

    return capturedToken.value ? '' : 'This invitation link is missing its token. Open the complete link from your invitation email.';
  });
  const [name, setName] = useState('');
  const [organization, setOrganization] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const validationRequest = useRef<Promise<InvitationValidationResponse> | null>(null);
  const submissionInProgress = useRef(false);

  useLayoutEffect(() => {
    const search = new URLSearchParams(location.search);
    if (!search.has('token')) {
      return;
    }

    search.delete('token');
    const remainingSearch = search.toString();
    navigate({
      pathname: location.pathname,
      search: remainingSearch ? `?${remainingSearch}` : '',
      hash: location.hash,
    }, { replace: true });
  }, [location.hash, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!capturedToken.value) {
      return;
    }

    const request = validationRequest.current ?? validateInvitation(capturedToken.value);
    validationRequest.current = request;
    let isCurrent = true;

    request.then((response) => {
      if (isCurrent) {
        setInvitation(response);
        setValidationError('');
      }
    }).catch((error: unknown) => {
      if (isCurrent) {
        setValidationError(validationErrorMessage(error));
      }
    });

    return () => {
      isCurrent = false;
    };
  }, [capturedToken.value]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (
      !invitation
      || !capturedToken.value
      || isAuthLoading
      || submissionInProgress.current
    ) {
      return;
    }

    const trimmedName = name.trim();
    const resolvedOrganization = invitation.organization?.trim() || organization.trim();

    if (!trimmedName) {
      setFormError('Enter your name.');
      return;
    }

    if (trimmedName.length > 120) {
      setFormError('Name must be 120 characters or fewer.');
      return;
    }

    if (!resolvedOrganization) {
      setFormError('Enter your organization.');
      return;
    }

    if (resolvedOrganization.length > 160) {
      setFormError('Organization must be 160 characters or fewer.');
      return;
    }

    if (password.length < 12 || password.length > 128) {
      setFormError('Password must be between 12 and 128 characters.');
      return;
    }

    if (password !== passwordConfirmation) {
      setFormError('Passwords do not match.');
      return;
    }

    setFormError('');
    submissionInProgress.current = true;
    setIsSubmitting(true);

    try {
      await acceptInvitation({
        token: capturedToken.value,
        name: trimmedName,
        organization: resolvedOrganization,
        password,
      });
      navigate('/dashboard', { replace: true });
    } catch (error: unknown) {
      setFormError(acceptanceErrorMessage(error));
      submissionInProgress.current = false;
      setIsSubmitting(false);
    }
  };

  const isLoading = Boolean(capturedToken.value && !invitation && !validationError);
  const hasInvitationOrganization = Boolean(invitation?.organization?.trim());

  return (
    <section className="invitation-page">
      <div className="invitation-panel">
        <p className="eyebrow">Account invitation</p>
        <h1>Set up your Strata account.</h1>

        {isLoading && (
          <p className="invitation-status" role="status">Checking your invitation...</p>
        )}

        {validationError && (
          <div className="invitation-error-state" role="alert">
            <h2>We could not open this invitation.</h2>
            <p>{validationError}</p>
          </div>
        )}

        {invitation && (
          <>
            <dl className="invitation-details" aria-label="Invitation details">
              <div>
                <dt>Email</dt>
                <dd>{invitation.email}</dd>
              </div>
              <div>
                <dt>Role</dt>
                <dd>{formatRole(invitation.role)}</dd>
              </div>
              {hasInvitationOrganization && (
                <div>
                  <dt>Organization</dt>
                  <dd>{invitation.organization}</dd>
                </div>
              )}
            </dl>

            <form className="invitation-form" onSubmit={handleSubmit} noValidate>
              <label htmlFor="invitation-name">Name</label>
              <input
                id="invitation-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                autoComplete="name"
                maxLength={120}
                required
              />

              {!hasInvitationOrganization && (
                <>
                  <label htmlFor="invitation-organization">Organization</label>
                  <input
                    id="invitation-organization"
                    value={organization}
                    onChange={(event) => setOrganization(event.target.value)}
                    autoComplete="organization"
                    maxLength={160}
                    required
                  />
                </>
              )}

              <label htmlFor="invitation-password">New password</label>
              <input
                id="invitation-password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                minLength={12}
                maxLength={128}
                aria-describedby="invitation-password-help"
                required
              />
              <p id="invitation-password-help" className="field-help">
                Use 12 to 128 characters with at least two of: lowercase, uppercase, number, or symbol.
              </p>

              <label htmlFor="invitation-password-confirmation">Confirm new password</label>
              <input
                id="invitation-password-confirmation"
                type="password"
                value={passwordConfirmation}
                onChange={(event) => setPasswordConfirmation(event.target.value)}
                autoComplete="new-password"
                minLength={12}
                maxLength={128}
                required
              />

              {formError && <p className="form-error" role="alert">{formError}</p>}
              <button type="submit" disabled={isSubmitting || isAuthLoading}>
                {isSubmitting ? 'Creating account...' : 'Create account'}
              </button>
            </form>
          </>
        )}
      </div>
    </section>
  );
}

export default AcceptInvitation;
